/**
 * useDynamicFilterOptions — builds the store-backed dynamic option data that
 * the shared filter field config (config/filterFields.ts) needs at render
 * time: queue/cycle/track/session/collection/package/cluster/exclusive
 * option lists, the Subject Type feature flag, and enabled custom fields.
 *
 * Used by FilterPanel.tsx (item list). Finding items for a pool now goes
 * through the item list too (DEP-034), so there is one filter vocabulary.
 */

import { useMemo } from 'react';
import type { DynamicOptionsMap } from '../config/filterFields';
import { useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';
import { useDegapProcessStore } from '../../../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../../../stores/prioritization/trackStore';
import { useSessionStore } from '../../../../stores/prioritization/sessionStore';
import { useCollectionsStore } from '../../../../stores/prioritization/collectionsStore';
import { useItemRelationshipsStore } from '../../../../stores/prioritization/itemRelationshipsStore';
import { useAttributesStore } from '../../../../stores/prioritization/attributesStore';
import { useFeatureFlag, FEATURE_FLAGS } from '../../../../lib/featureFlags';

import { useItemsStore } from '../../../../stores/prioritization/itemsStore';
import { useUserNameMap } from '../../../../hooks/useUserNameMap';
export function useDynamicFilterOptions() {
  const isSubjectTypesEnabled = useFeatureFlag(FEATURE_FLAGS.SUBJECT_TYPES);

  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const tracks = useTrackStore((s) => s.tracks);
  const sessions = useSessionStore((s) => s.sessions);
  const collectionsRaw = useCollectionsStore((s) => s.collections);
  const relationshipGroups = useItemRelationshipsStore((s) => s.groups);
  const attributes = useAttributesStore((s) => s.attributes);

  const queueOptions = useMemo(
    () =>
      Array.from(orgUnits.values())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((u) => ({ value: u.id, label: u.displayName || u.name })),
    [orgUnits]
  );
  const cycleOptions = useMemo(
    () => (cycles ?? []).map((c) => ({ value: c.id, label: c.name })),
    [cycles]
  );
  const trackOptions = useMemo(
    () => (tracks ?? []).map((t) => ({ value: t.id, label: t.name })),
    [tracks]
  );
  const sessionOptions = useMemo(
    () =>
      (sessions ?? []).map((s) => ({
        value: s.id,
        label: s.name || s.title || `Session ${s.id.slice(0, 6)}`,
      })),
    [sessions]
  );
  const collectionOptions = useMemo(
    () =>
      [...(collectionsRaw ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ value: c.id, label: c.name })),
    [collectionsRaw]
  );
  const packageOptions = useMemo(
    () =>
      (relationshipGroups ?? [])
        .filter((g) => g.type === 'package' && g.status !== 'dissolved')
        .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
        .map((g, i) => ({ value: g.id, label: g.title?.trim() || `Package ${i + 1}` })),
    [relationshipGroups]
  );
  const clusterOptions = useMemo(
    () =>
      (relationshipGroups ?? [])
        .filter((g) => g.type === 'cluster' && g.status !== 'dissolved')
        .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
        .map((g, i) => ({ value: g.id, label: g.title?.trim() || `Cluster ${i + 1}` })),
    [relationshipGroups]
  );
  const exclusiveOptions = useMemo(
    () =>
      (relationshipGroups ?? [])
        .filter((g) => g.type === 'exclusive' && g.status !== 'dissolved')
        .sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))
        .map((g, i) => ({ value: g.id, label: g.title?.trim() || `Competing group ${i + 1}` })),
    [relationshipGroups]
  );

  // Owners: the people who own items today, named through the shared lookup.
  const items = useItemsStore((s) => s.items);
  const ownerIds = useMemo(
    () => [...new Set(items.map((i) => i.ownerId).filter((id): id is string => !!id))].sort(),
    [items]
  );
  const ownerNames = useUserNameMap(ownerIds);
  const ownerOptions = useMemo(
    () => ownerIds.map((id) => ({ value: id, label: ownerNames[id] ?? id })).sort((a, b) => a.label.localeCompare(b.label)),
    [ownerIds, ownerNames]
  );

  const dynamicOptionsMap: DynamicOptionsMap = useMemo(
    () => ({
      queue: queueOptions,
      orgUnit: queueOptions,
      ownerId: ownerOptions,
      cycleId: cycleOptions,
      trackId: trackOptions,
      sessionId: sessionOptions,
      inCollection: collectionOptions,
      inPackage: packageOptions,
      inCluster: clusterOptions,
      inExclusive: exclusiveOptions,
    }),
    [queueOptions, ownerOptions, cycleOptions, trackOptions, sessionOptions, collectionOptions, packageOptions, clusterOptions, exclusiveOptions]
  );

  const enabledCustomFields = useMemo(
    () =>
      attributes.filter(
        (a) => a.isCustomField === true && a.enabled !== false && (a.objectType ?? 'item') === 'item'
      ),
    [attributes]
  );

  return { dynamicOptionsMap, isSubjectTypesEnabled, enabledCustomFields };
}
